import { useEffect, useCallback, useMemo, useState, ChangeEvent } from 'react';
import debounce from 'lodash/debounce';

/* Integrations */
import useSplit from '@integrations/split/useSplit';
import { SPLITIONAME_USER_EMAIL_VALIDATOR, SplitTreatmentValuesEnum } from '@integrations/split/split.constants';

/* Utils */
import { Logger } from '@utils/logger';

/* Apis */
import { user } from '@api/apis';
import getQueryOutsideTree from '@store/queries/utils/getQueryOutsideTree';
import { FieldError } from 'react-hook-form';

/**
 * useValidateEmailExternal()
 *
 * This custom hook is used for validating email addresses through a third-party API (Emailable).
 * It provides functionality to send a request to the API and manage the response.
 *
 * Example:
 * const { validateEmail, emailValidation } = useValidateEmailExternal();
 * validateEmail('test@example.com');
 */
export const useValidateEmailExternal = () => {
  const [error, setError] = useState<{ err: string } | null>(null);
  const [emailValidationState, setEmailValidationState] = useState({
    valid: null,
    did_you_mean: null,
    email: '',
  });

  const resetState = () => {
    setError(null);
    setEmailValidationState({
      valid: null,
      did_you_mean: null,
      email: '',
    });
  };
  const validateEmail = useCallback(async (email: string) => {
    resetState();
    try {
      const response = await user.validateEmailExternal({ email });
      if (response.err) {
        throw new Error(response.err);
      }
      const { data } = response;
      setEmailValidationState(data);
      return data;
    } catch (err: unknown) {
      Logger('useValidateEmailExternal - validateEmail()')(err as string);
      setEmailValidationState({
        valid: null,
        did_you_mean: null,
        email: '',
      });
      let errDetails = null;
      if (err instanceof Error) {
        errDetails = { err: err.message };
      }
      setError(errDetails);
      return errDetails;
    }
  }, []);

  return {
    validateEmail,
    error,
    emailValidationState,
  };
};

type TPerformEmailValidationProps = {
  setValue: BaseAnyFunction;
  setError: BaseAnyFunction;
  fieldName: string;
  email: string;
  suppressError: boolean;
  useValidationService: boolean;
  onSuccess: BaseAnyFunction | null;
  validateEmail: Function;
};
const performEmailValidation = async ({
  setValue,
  setError,
  fieldName,
  email,
  suppressError,
  useValidationService,
  onSuccess,
  validateEmail,
}: TPerformEmailValidationProps) => {
  // Early exit if validation service is not used, regardless of the email presence
  const split = getQueryOutsideTree(['split', SPLITIONAME_USER_EMAIL_VALIDATOR]);
  const isSplitOn = split?.treatment === SplitTreatmentValuesEnum.ON;

  if (!useValidationService) {
    if (onSuccess) onSuccess();
    return;
  }

  // Proceed only if there is an email to validate
  if (!email) return;

  // Proceed only if the split is on
  if (!isSplitOn) {
    if (onSuccess) onSuccess();
    return;
  }

  // Proceed only if the email contains an '@'. Prevents invalid emails from being validated
  if (!email.includes('@')) return;

  // Proceed with using the external validation service
  const response = await validateEmail(email);

  // Handle Network Error
  if (response.err) {
    setError(fieldName, 'An error occurred. Please try again.');
    return;
  }

  // Handle Validation Error
  if (!response.valid) {
    if (response.did_you_mean) {
      setError(fieldName, '');
      return;
    } // Suggestion will always take precedence over error
    if (!suppressError) {
      setError(fieldName, 'Enter a valid email address');
    }
    return;
  }

  if (onSuccess) onSuccess();
};

/**
 * useExternalEmailCheck()
 *
 * Custom hook for validating emails with an external service and Formik. Updates Formik's field
 * errors based on external validation results.
 *
 * Note: The external service may suggest alternative emails that also fail validation.
 *
 * @param {object} formik - The Formik instance.
 * @param {string} fieldName - The field name to validate (e.g., 'email').
 * @param {Function} onSuccess - Callback after successful validation.
 * @param {boolean} useValidationService - Flag to use the external validation service.
 * @returns {object} - Contains 'validateEmailWithConfig', 'emailValidationState', 'useSuggestion', and 'handleOnChange'.
 *
 * Usage:
 * const { validateEmailWithConfig, emailValidationState, useSuggestion, handleOnChange } = useExternalEmailCheck({
 *   formik,
 *   fieldName: 'email',
 *   onSuccess: () => {...},
 *   useValidationService: someConditional ? true : false,
 * });
 */

type TUseExternalEmailCheckProps = {
  setValue: BaseAnyFunction;
  setError: BaseAnyFunction;
  fieldName: string;
  onSuccess?: BaseAnyFunction;
  useValidationService?: boolean;
  debounceDelay?: number;
};

export const useExternalEmailCheck = ({
  setValue,
  setError,
  fieldName,
  onSuccess = () => {},
  useValidationService = true,
  debounceDelay = 1000, // Default debounce delay in milliseconds
}: TUseExternalEmailCheckProps) => {
  const { validateEmail, emailValidationState } = useValidateEmailExternal();

  useSplit(SPLITIONAME_USER_EMAIL_VALIDATOR);

  const [dismissedSuggestion, setDismissedSuggestion] = useState<string | null>(null);

  const resolvedEmailValidationState = useMemo(() => {
    if (dismissedSuggestion && emailValidationState?.did_you_mean === dismissedSuggestion) {
      return { ...emailValidationState, did_you_mean: null };
    }
    return emailValidationState;
  }, [dismissedSuggestion, emailValidationState]);

  const sharedConfig = useMemo(
    () => ({ setValue, setError, fieldName, useValidationService, validateEmail }),
    [setValue, setError, fieldName, useValidationService, validateEmail],
  );

  /** Call validateEmail for manual submission. */
  type TValidateEmailProps = {
    email: string;
    suppressError?: boolean;
  };
  const validateEmailWithConfig = async ({ email, suppressError = false }: TValidateEmailProps) => {
    await performEmailValidation({
      ...sharedConfig,
      email: email?.trim() || '',
      suppressError,
      onSuccess,
    });
  };

  /** Call validateEmail for auto-submit. */
  const validateEmailWithConfigAutoSubmit = useCallback(
    async ({ email, suppressError = true }: TValidateEmailProps) => {
      await performEmailValidation({
        ...sharedConfig,
        email: email?.trim() || '',
        suppressError,
        onSuccess: null, // Don't call onSuccess for auto-submit
      });
    },
    [sharedConfig],
  );

  /** A debounced version of validateEmailWithConfig used for auto-submit */
  const debouncedValidateEmailWithConfig = debounce(
    ({ email, suppressError }) => validateEmailWithConfigAutoSubmit({ email, suppressError }),
    debounceDelay,
    {
      trailing: true,
      leading: false,
    },
  );

  const useSuggestion = useCallback(() => {
    if (!resolvedEmailValidationState?.did_you_mean) return;
    setDismissedSuggestion(resolvedEmailValidationState.did_you_mean);
    setError(fieldName, ''); // clear error
    setValue(fieldName, resolvedEmailValidationState.did_you_mean);
    validateEmailWithConfigAutoSubmit({
      email: resolvedEmailValidationState.did_you_mean,
      suppressError: false,
    }); // Test validity again. Sometimes the services suggests an invalid email
  }, [fieldName, resolvedEmailValidationState, setError, setValue, validateEmailWithConfigAutoSubmit]);

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setError(fieldName, '');
    setValue(fieldName, event.target.value);
    debouncedValidateEmailWithConfig({ email: event.target.value, suppressError: true });
  };

  useEffect(
    () => () => {
      debouncedValidateEmailWithConfig.cancel();
    },
    [debouncedValidateEmailWithConfig],
  );

  return {
    /** State of the last email validation call */
    emailValidationState: resolvedEmailValidationState,
    /** Call the external validation service */
    validateEmailWithConfig,
    /** Populate the email form field with the suggested email */
    useSuggestion,
    /** Wrapper around formik.handleChange to call the validation service */
    handleOnChange,
  };
};

/**
 * Not a hook. A util function to set an error in a form.
 *
 * @param {(name: string, error: FieldError) => void} setError
 * @returns {(fieldName: T, error: string) => void}
 */
export const getSetHookFormError =
  <T extends string>(setError: (name: T, error: FieldError) => void) =>
  (fieldName: T, error: string) => {
    setError(fieldName, {
      type: 'manual',
      message: error,
    });
  };
